// R58 — A ROOM THAT RENDERS WITH NO OFFERINGS IN IT, AND NOTHING RED ANYWHERE.
//
// THE HUNT is the pointer's, one size down from R53: four `r.ok ? r.json() : { reports: [] }` sites
// survived that round because they are DIFFERENT ROUTES. Measured 2026-08-01 there are **FIVE**, not
// four — `/api/astrology/deep-reports` was missed by the census because it sits fourteen lines below
// its three neighbours — and every one of them paints a room's BUTTON GRID:
//
//     {miniConfigs.length ? (<section className="mini-reports">…</section>) : null}
//
// **SO A FAILED READ IS NOT AN EMPTY GRID, IT IS A SECTION THAT IS NOT THERE.** Four whole bands of
// the astrology room — the mini-readings, the predictions, the deep readings and the traditions — and
// the numerology room's mini row, gone, with no error, no log and nothing on screen to tell her the
// difference between a reading this house does not offer and one it could not ask about. That is
// R25's argument (an absent control has no error state) at ROOM scale, and 14-B6c already
// photographed these grids as the half of those rooms that does NOT translate, so they are the most
// looked-at surface in the house that nothing was watching.
//
// **THE FABRICATED FALLBACK IS THE SAME DEFECT R55 AND R57 FIXED**: `{ reports: [] }` wears the
// shape of a real answer, so `Array.isArray(d.reports)` says yes and the room paints "there is
// nothing here". R18's distinction three ways at once — a menu that HAS NOT LOADED YET, a menu that
// FAILED, and a menu the server really has no rows for were all the same `[]`.
//
// **WHAT THESE FIVE ROUTES CANNOT DO IS REFUSE.** Every one is a synchronous read of a committed
// catalog (`server/data/*.json`, loaded at boot) — no session, no database, no vendor — so unlike
// R53/R54/R55/R57 there is no 401 and no `{ ok: false }` to tell apart. The whole failure surface is
// TRANSPORT: the request did not land, or what came back was not the catalog. So the answer here is
// deliberately `T[] | null` and not a four-way one; inventing a refusal branch for a route that has
// none would be a second answer key for a question nobody asks.
//
// **THE PAYLOAD KEY IS DECLARED BESIDE THE PATH BECAUSE IT IS NOT THE SAME AT ALL FIVE.**
// `predictions` sends `{ predictions }` and the other four send `{ reports }`, which was hand-typed
// once per call site — R28/R29's shape exactly, where one member of a roster differs and the
// difference is held by nobody. The paths are held against the SERVER'S own routed URLs by
// `scripts/r58-reading-menus.mjs` (R13/R14/R15: a cross-boundary law opens both sides).
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

// THE FIVE MENUS THIS HOUSE SERVES, and what each one calls its rows.
export const READING_MENUS = {
  "astrology-minis": { path: "/api/astrology/mini-reports", key: "reports" },
  "astrology-traditions": { path: "/api/astrology/traditions", key: "reports" },
  "astrology-predictions": { path: "/api/astrology/predictions", key: "predictions" },
  "astrology-deep": { path: "/api/astrology/deep-reports", key: "reports" },
  "numerology-minis": { path: "/api/numerology/mini-reports", key: "reports" },
} as const;

export type ReadingMenu = keyof typeof READING_MENUS;

// A menu nobody registered is a FALLTHROUGH, and it answers null rather than throwing: R19's lesson,
// where a fixture that threw on an unexpected mode lost every check it had already recorded. A room
// asking for a menu that does not exist gets the same honest "this did not arrive" a dead network
// gets, which is exactly what it is.
export function readingMenuRoute(menu: string): { path: string; key: string } | null {
  return (READING_MENUS as Record<string, { path: string; key: string }>)[menu] || null;
}

// THE READ. **A FAILED READ IS NOT AN EMPTY MENU** (R18/R53/R55/R57): null, so the room keeps
// whatever it was already showing and says so, rather than quietly losing a whole band of itself.
// The server's own empty list is a real `{ reports: [] }` — a catalog with no rows — so the two are
// told apart by the SHAPE, not by the status code, and the fabricated fallback that used to wear
// that shape is gone.
//
// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer without a
// browser, a session or a running server.
export async function listReadingMenu<T>(menu: ReadingMenu, doFetch: typeof fetch = fetch): Promise<T[] | null> {
  const route = readingMenuRoute(menu);
  if (!route) return null;
  try {
    const res = await doFetch(route.path);
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    const rows = data ? data[route.key] : null;
    return Array.isArray(rows) ? (rows as T[]) : null;
  } catch {
    return null;
  }
}

// The sentence, derived rather than typed per room. **A SUCCESS SAYS NOTHING** — the buttons being
// there IS the answer (R51/R53/R54/R55/R57 on the same question). It takes the count of menus that
// failed rather than a boolean so the two rooms compose it the same way: the astrology room asks
// four routes and the numerology room asks one, and neither of them wants four copies of one line.
export function readingMenuNote(failed: number, copy: { failed: string }): string {
  return failed > 0 ? copy.failed : "";
}
